//  NotificationBell 
// Reads agent_alerts and surfaces them as in-app notifications.
// Unread state tracked in localStorage (per user).
// Shows badge count on bell; clicking opens a slide-in panel.

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell, BarChart2, FileText, BookOpen,
  X, ExternalLink, AlertTriangle, ShieldAlert, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

//  Types 

interface RawAlert {
  agent: string;
  alert_type: string;
  content: Record<string, any>;
  created_at: string;
}

interface AppNotification {
  key: string;            // agent key — one notification per agent
  title: string;
  body: string;
  icon: React.ElementType;
  color: string;
  link: string;
  created_at: string;
}

//  Agent  notification mapping 

function alertToNotif(alert: RawAlert): AppNotification | null {
  const c = alert.content ?? {};
  switch (alert.agent) {
    case "risk":
      if (!["rule_breach", "daily_limit_hit", "drawdown_warning", "prop_violation"].includes(alert.alert_type)) return null;
      return {
        key: `risk-${alert.alert_type}`,
        title:
          alert.alert_type === "rule_breach"     ? "Rule Breach Detected"      :
          alert.alert_type === "daily_limit_hit" ? "Daily Loss Limit Hit"       :
          alert.alert_type === "prop_violation"  ? "Prop Firm Violation"        :
          "Large Drawdown Warning",
        body: c.message ?? c.detail ?? "Check your risk dashboard immediately.",
        icon: ShieldAlert,
        color: "rgba(239,68,68,0.9)",
        link: "/prop-guard",
        created_at: alert.created_at,
      };
    case "pattern":
      if (alert.alert_type !== "pattern_summary") return null;
      if (c.leak_count != null && Number(c.leak_count) === 0) return null;
      return {
        key: "pattern",
        title: "Trading Leaks Detected",
        body: c.leak_count
          ? `${c.leak_count} leak${Number(c.leak_count) !== 1 ? "s" : ""} found in recent trades. Biggest: ${c.biggest_leak ?? "review journal"}.`
          : "Significant patterns found in your recent trades.",
        icon: BarChart2,
        color: "rgba(249,115,22,0.8)",
        link: "/journal",
        created_at: alert.created_at,
      };
    case "report":
      if (alert.alert_type !== "weekly_report") return null;
      return {
        key: "report",
        title: "Weekly Report Ready",
        body: c.overall_grade
          ? `Grade: ${c.overall_grade.toUpperCase()} · ${c.one_sentence_verdict ?? ""}`
          : "Your weekly trading report has been generated.",
        icon: FileText,
        color: "rgba(34,197,94,0.8)",
        link: "/edge-score",
        created_at: alert.created_at,
      };
    case "calendar":
      if (alert.alert_type !== "weekly_briefing") return null;
      if (c.week_risk_level !== "high") return null;
      return {
        key: "calendar",
        title: "High-Risk Week Ahead",
        body: c.session_note
          ? `High-impact events this week. ${c.session_note}`
          : "Multiple high-impact economic events this week — adjust position sizing.",
        icon: AlertTriangle,
        color: "rgba(239,68,68,0.8)",
        link: "/command-center",
        created_at: alert.created_at,
      };
    case "journal":
      if (alert.alert_type !== "journal_drafts_ready") return null;
      return {
        key: "journal",
        title: "Journal Drafts Ready",
        body: c.drafts_created
          ? `${c.drafts_created} AI draft${Number(c.drafts_created) !== 1 ? "s" : ""} generated for your recent trades.`
          : "AI journal drafts are ready to review.",
        icon: BookOpen,
        color: "rgba(147,197,253,0.8)",
        link: "/journal",
        created_at: alert.created_at,
      };
    default:
      return null;
  }
}

// Friendly label for the notification's click-through target.
function linkLabel(link: string): string {
  switch (link) {
    case "/prop-guard":     return "Prop Guard";
    case "/journal":        return "Journal";
    case "/edge-score":     return "Edge Score";
    case "/command-center": return "Command Center";
    default:                return "app";
  }
}

//  localStorage helpers

function getSeenMap(userId: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(`mm-notif-seen-${userId}`) ?? "{}");
  } catch {
    return {};
  }
}

function markSeen(userId: string, key: string, ts: string) {
  const map = getSeenMap(userId);
  map[key] = ts;
  localStorage.setItem(`mm-notif-seen-${userId}`, JSON.stringify(map));
}

function markAllSeen(userId: string, notifs: AppNotification[]) {
  const map = getSeenMap(userId);
  for (const n of notifs) map[n.key] = n.created_at;
  localStorage.setItem(`mm-notif-seen-${userId}`, JSON.stringify(map));
}

function isUnread(userId: string, notif: AppNotification): boolean {
  const map = getSeenMap(userId);
  const seen = map[notif.key];
  if (!seen) return true;
  return new Date(notif.created_at) > new Date(seen);
}

function formatAgo(ts: string): string {
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

//  Component 

export function NotificationBell({
  isMobile = false,
  rowMode = false,
  labelVisible = false,
}: {
  isMobile?: boolean;
  rowMode?: boolean;
  labelVisible?: boolean;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const userId = user?.id ?? null;

  // Request browser notification permission once when user is logged in
  useEffect(() => {
    if (!userId) return;
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [userId]);

  // Track push notifications already fired (per-session + localStorage dedup)
  const firedTagsRef = useRef<Set<string>>(new Set());

  // Background polling — fire OS notifications for new unread alerts every 60s
  useEffect(() => {
    if (!userId) return;
    // Restore previously fired tags from localStorage
    try {
      const saved = JSON.parse(localStorage.getItem(`mm-notif-pushed-${userId}`) ?? "[]");
      if (Array.isArray(saved)) saved.forEach((k: string) => firedTagsRef.current.add(k));
    } catch { /* ignore */ }

    const checkAndPush = async () => {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      const { data } = await supabase
        .from("agent_alerts")
        .select("agent,alert_type,content,created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (!data) return;
      const seen = new Set<string>();
      for (const raw of data as RawAlert[]) {
        if (seen.has(raw.agent)) continue;
        const notif = alertToNotif(raw);
        if (!notif) continue;
        seen.add(raw.agent);
        if (!isUnread(userId, notif)) continue;
        const dedupeKey = `${notif.key}::${notif.created_at}`;
        if (firedTagsRef.current.has(dedupeKey)) continue;
        new Notification(notif.title, {
          body: notif.body,
          tag: `mm-${dedupeKey}`,
          icon: "/favicon.ico",
        });
        firedTagsRef.current.add(dedupeKey);
      }
      try {
        localStorage.setItem(`mm-notif-pushed-${userId}`, JSON.stringify([...firedTagsRef.current]));
      } catch { /* ignore */ }
    };

    checkAndPush();
    const interval = setInterval(checkAndPush, 60_000);
    return () => clearInterval(interval);
  }, [userId]);

  const loadNotifs = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from("agent_alerts")
      .select("agent,alert_type,content,created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (!data) return;

    const seen = new Set<string>();
    const result: AppNotification[] = [];
    for (const raw of data as RawAlert[]) {
      if (seen.has(raw.agent)) continue;
      const notif = alertToNotif(raw);
      if (notif) {
        seen.add(raw.agent);
        result.push(notif);
      }
    }
    result.sort((a, b) => {
      const au = isUnread(userId, a);
      const bu = isUnread(userId, b);
      if (au !== bu) return au ? -1 : 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    setNotifs(result);
  }, [userId]);

  useEffect(() => { loadNotifs(); }, [loadNotifs]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const unreadCount = userId ? notifs.filter(n => isUnread(userId, n)).length : 0;

  const handleOpen = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) loadNotifs();
  };

  // Expanding a notification marks it read and shows its full detail + a
  // "view in <page>" action — click to see exactly what it is, then click through.
  const handleToggleExpand = (notif: AppNotification) => {
    if (userId) markSeen(userId, notif.key, notif.created_at);
    setExpandedKey(prev => (prev === notif.key ? null : notif.key));
    setNotifs(prev => [...prev]);
  };

  const handleClickNotif = (notif: AppNotification) => {
    if (userId) markSeen(userId, notif.key, notif.created_at);
    setNotifs(prev => [...prev]);
    setOpen(false);
    navigate(notif.link);
  };

  const handleMarkAllRead = () => {
    if (!userId) return;
    markAllSeen(userId, notifs);
    setNotifs(prev => [...prev]);
  };

  // Panel — positioned absolute to escape overflow:hidden on sidebar
  const panelContent = open ? (
    <div
      ref={panelRef}
      className="flex flex-col z-[9999]"
      style={{
        ...(isMobile
          ? {
              position: "fixed",
              top: 56,
              left: 8,
              right: 8,
              width: "auto",
              maxHeight: "calc(100dvh - 72px)",
            }
          : {
              position: "absolute",
              width: 320,
              maxHeight: 480,
              left: "calc(100% + 8px)",
              bottom: 0,
            }),
        background: "#000",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
      }}
    >
      {/* Panel header */}
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono font-bold uppercase tracking-[0.08em]" style={{ color: "rgba(255,255,255,0.6)" }}>
            Alerts
          </span>
          {unreadCount > 0 && (
            <span
              className="text-[8px] font-mono px-1.5 py-0.5 rounded-full"
              style={{ background: "rgba(239,68,68,0.15)", color: "rgba(239,68,68,0.8)" }}
            >
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-[9px] font-mono opacity-40 hover:opacity-70 transition-opacity"
            >
              mark all read
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="opacity-30 hover:opacity-60 transition-opacity"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto">
        {notifs.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell size={20} className="mx-auto mb-2 opacity-20" />
            <p className="text-[10px] font-mono" style={{ color: "rgba(255,255,255,0.25)" }}>
              No important alerts.
            </p>
            <p className="text-[9px] mt-1" style={{ color: "rgba(255,255,255,0.15)" }}>
              Rule breaches, limit hits, and weekly reports appear here.
            </p>
          </div>
        ) : (
          notifs.map((notif, idx) => {
            const Icon = notif.icon;
            const unread = userId ? isUnread(userId, notif) : false;
            const isLast = idx === notifs.length - 1;
            const expanded = expandedKey === notif.key;
            return (
              <div
                key={notif.key}
                style={{
                  borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.05)",
                  background: unread ? "rgba(255,255,255,0.015)" : "transparent",
                }}
              >
                <button
                  onClick={() => handleToggleExpand(notif)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-white/[0.04]"
                >
                  <div
                    className="shrink-0 w-7 h-7 rounded flex items-center justify-center mt-0.5"
                    style={{ background: `${notif.color}15`, border: `1px solid ${notif.color}25` }}
                  >
                    <Icon size={13} style={{ color: notif.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p
                        className="text-[11px] font-semibold truncate"
                        style={{ color: unread ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.55)" }}
                      >
                        {notif.title}
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {unread && (
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: "rgba(239,68,68,0.8)" }}
                          />
                        )}
                        <span className="text-[9px] font-mono" style={{ color: "rgba(255,255,255,0.2)" }}>
                          {formatAgo(notif.created_at)}
                        </span>
                        <ChevronDown
                          size={11}
                          className="shrink-0 transition-transform"
                          style={{ color: "rgba(255,255,255,0.25)", transform: expanded ? "rotate(180deg)" : "none" }}
                        />
                      </div>
                    </div>
                    <p
                      className={cn("text-[10px] leading-snug", !expanded && "line-clamp-2")}
                      style={{ color: "rgba(255,255,255,0.38)" }}
                    >
                      {notif.body}
                    </p>
                  </div>
                </button>
                {expanded && (
                  <div className="px-4 pb-3 pl-14">
                    <button
                      onClick={() => handleClickNotif(notif)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-opacity hover:opacity-80"
                      style={{ color: notif.color, border: `1px solid ${notif.color}40`, background: `${notif.color}12` }}
                    >
                      View in {linkLabel(notif.link)} <ExternalLink size={9} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Panel footer */}
      {notifs.length > 0 && (
        <div
          className="px-4 py-2 shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-[9px] font-mono text-center" style={{ color: "rgba(255,255,255,0.2)" }}>
            Important alerts only · {notifs.length} active
          </p>
        </div>
      )}
    </div>
  ) : null;

  //  rowMode: render as a sidebar bottom row 
  if (rowMode) {
    return (
      <div
        ref={containerRef}
        className="relative flex items-center h-9 px-3 gap-3 hover:bg-white/[0.05] transition-colors duration-150 cursor-pointer"
        onClick={handleOpen}
      >
        <div className="relative shrink-0">
          <Bell
            size={16}
            strokeWidth={1.5}
            style={{ color: open ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.35)" }}
          />
          {unreadCount > 0 && (
            <span
              className="absolute flex items-center justify-center rounded-full text-[7px] font-bold leading-none"
              style={{
                width: 12,
                height: 12,
                top: -5,
                right: -5,
                background: "rgba(239,68,68,0.9)",
                color: "#fff",
              }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>
        <span
          className={cn(
            "text-[11px] whitespace-nowrap transition-opacity duration-100",
            labelVisible ? "opacity-100" : "opacity-0"
          )}
          style={{ color: "rgba(255,255,255,0.35)" }}
        >
          Notifications
        </span>
        {panelContent}
      </div>
    );
  }

  //  isMobile: compact bell button in header 
  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>} className="relative">
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center transition-colors duration-150 hover:bg-white/[0.05]"
        style={{ width: 36, height: 36 }}
        title="Agent notifications"
      >
        <Bell
          size={16}
          strokeWidth={1.5}
          style={{ color: open ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.35)" }}
        />
        {unreadCount > 0 && (
          <span
            className="absolute flex items-center justify-center rounded-full text-[8px] font-bold leading-none"
            style={{
              width: 14,
              height: 14,
              top: 4,
              right: 4,
              background: "rgba(239,68,68,0.9)",
              color: "#fff",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {panelContent}
    </div>
  );
}
